import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { PlayCircle } from "lucide-react";

// Shows the game cover, and on hover plays a short muted, looping trailer clip.
// Supports Steam HLS streams and YouTube trailers. The stream is only loaded
// while hovered to keep the page light.
export const TrailerHoverPreview = ({ image, alt, hls: hlsSrc, youtubeId, className = "" }) => {
  const videoRef = useRef(null);
  const hlsInst = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [ready, setReady] = useState(false);

  const hasPreview = Boolean(hlsSrc || youtubeId);

  useEffect(() => {
    if (!hovered || !hlsSrc) return;
    const video = videoRef.current;
    if (!video) return;

    const tryPlay = () => {
      setReady(true);
      video.play().catch(() => {});
    };

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = hlsSrc;
    } else if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true });
      hls.loadSource(hlsSrc);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, tryPlay);
      hlsInst.current = hls;
    } else {
      video.src = hlsSrc;
    }

    video.addEventListener("loadedmetadata", tryPlay);
    video.addEventListener("canplay", tryPlay);

    return () => {
      video.removeEventListener("loadedmetadata", tryPlay);
      video.removeEventListener("canplay", tryPlay);
      if (hlsInst.current) {
        hlsInst.current.destroy();
        hlsInst.current = null;
      }
      setReady(false);
    };
  }, [hovered, hlsSrc]);

  // Reset fade when leaving hover (covers the YouTube branch too).
  useEffect(() => {
    if (!hovered) setReady(false);
  }, [hovered]);

  const ytSrc = youtubeId
    ? `https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&mute=1&controls=0&loop=1&playlist=${youtubeId}&modestbranding=1&playsinline=1&rel=0&disablekb=1&fs=0&iv_load_policy=3`
    : null;

  return (
    <div
      className={`w-full h-full relative ${className}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <img
        src={image}
        alt={alt}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
      />
      {hlsSrc && hovered && (
        <video
          ref={videoRef}
          muted
          loop
          playsInline
          data-testid="trailer-preview-video"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
            ready ? "opacity-100" : "opacity-0"
          }`}
        />
      )}
      {!hlsSrc && youtubeId && hovered && (
        <iframe
          src={ytSrc}
          title={alt || "Trailer preview"}
          data-testid="trailer-preview-youtube"
          onLoad={() => setReady(true)}
          frameBorder="0"
          allow="autoplay; encrypted-media"
          className={`absolute inset-0 w-full h-full pointer-events-none transition-opacity duration-500 ${
            ready ? "opacity-100" : "opacity-0"
          }`}
        />
      )}
      {hasPreview && (
        <span className="absolute top-3 left-3 z-10 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/55 text-white text-xs font-semibold backdrop-blur-sm opacity-100 group-hover:opacity-0 transition-opacity duration-300">
          <PlayCircle className="w-3.5 h-3.5" /> Hover to preview
        </span>
      )}
    </div>
  );
};
